import type { IButton } from "@Components/Button";
import { Button } from "@Components/Button";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { ErrorContainer } from "@Components/ErrorContainer";
import type { ILink } from "@Components/Link";
import { Link } from "@Components/Link";
import { Loading } from "@Components/Loading";
import { AjaxMethod } from "@Components/Modules/Ajax";
import type { IErrors } from "@Components/Modules/ErrorHandler";
import { ErrorType, clearAllErrors, setValidate } from "@Components/Modules/ErrorHandler";
import { requestTrack } from "@Components/Modules/Helpers";
import { isNullOrEmpty, isPasswordValid } from "@Components/Modules/Validations";
import type { ITextbox } from "@Components/Textbox";
import { TextboxType } from "@Components/Textbox";
import type { IValidator } from "@Components/Validator";

import type { INavigationModalActions } from "../common/NavigationModal";
import type { IInputTracker } from "../telemetry/InputTracker";
import { TextboxTracker } from "../telemetry/InputTracker";
import type { IEmailConfirmationFields } from "./EmailConfirmation";
import type { IResetPasswordForm } from "./ResetPasswordForm";
import { ResetPasswordForm } from "./ResetPasswordForm";

import * as cssNavModal from "../common/NavigationModal.module.css";
import * as css from "./PasswordEntryForm.module.css";

export interface IPasswordEntryForm
    extends IComponentAjax<boolean>, INavigationModalActions<IEmailConfirmationFields> {
    title: string;
    passwordLabel: string;
    passwordTextbox: IInputTracker<ITextbox>;
    forgotPasswordLink: ILink;
    button: IButton;
    passwordTextboxRequiredError: IValidator;
    passwordTextboxInvalidError: IValidator;
    resetPasswordForm: IResetPasswordForm;
    ajaxVisualAction: string;
}

interface IPasswordEntryFormState extends IComponentAjaxState {
    passwordTextboxValue: string;
    disabledButton: boolean;
    showResetPasswordForm: boolean;
}

export class PasswordEntryForm extends ComponentAjax<boolean, IPasswordEntryForm, IPasswordEntryFormState> {
    public static defaultProps: IPasswordEntryForm = {
        button: Button.defaultProps,
    } as IPasswordEntryForm;

    public errors: IErrors = {
        default: { ...this.defaultErrors().default, type: ErrorType.Validator },
        passwordTextboxRequiredError: {
            name: "password-required",
            type: ErrorType.Validator,
            targetRef: "passwordValue-textbox",
            container: { ref: "passwordValue-textbox-container" },
            onValidate: () => {
                if (isNullOrEmpty(this.state.passwordTextboxValue) === false) {
                    return true;
                }
                return false;
            },
        },
        passwordTextboxInvalidError: {
            name: "password-invalid",
            type: ErrorType.Validator,
            targetRef: "passwordValue-textbox",
            container: { ref: "passwordValue-textbox-container" },
            onValidate: () => isPasswordValid(this.state.passwordTextboxValue),
        },
    };

    constructor(props: IPasswordEntryForm) {
        super({ ...props, ajaxMethod: AjaxMethod.Post });

        const fields = this.props.fieldValues;
        if (fields) {
            this.state = {
                isLoading: false,
                passwordTextboxValue: fields.passwordValue || "",
                disabledButton: fields.passwordValue === undefined || fields.passwordValue === null,
                showResetPasswordForm: false,
            };
        } else {
            this.state = {
                isLoading: false,
                passwordTextboxValue: "",
                disabledButton: false,
                showResetPasswordForm: false,
            };
        }
    }

    public componentDidMount() {
        requestTrack(this.props.ajaxVisualAction);
    }

    public render() {
        return (
            <div
                {...this.rootDefaultAttributes(css.passwordEntryFormComponent, cssNavModal.standardModalForm)}
            >
                <h1 className={cssNavModal.standardModalTitle}>{this.props.title}</h1>
                <div className={cssNavModal.standardModalFormBody}>
                    {this.renderTextBox()}
                    {this.props.forgotPasswordLink && (
                        <Link {...this.props.forgotPasswordLink} onClick={this.onClickForgotPassword} />
                    )}
                </div>
                <Button
                    {...this.props.button}
                    disabled={this.state.disabledButton || isNullOrEmpty(this.state.passwordTextboxValue)}
                    onClick={this.submitForm}
                />
                {this.state.isLoading && <Loading isModal />}
                {this.state.showResetPasswordForm && (
                    <ResetPasswordForm
                        {...this.props.resetPasswordForm}
                        onClose={this.closeResetPasswordForm}
                    />
                )}
            </div>
        );
    }

    protected ajaxFail(): boolean {
        this.setState(() => ({ isLoading: false }));
        return true;
    }

    protected ajaxSuccess(response: boolean) {
        this.setState({ isLoading: false });

        if (response) {
            if (this.props.onSubmit) {
                if (this.props.saveValues) {
                    this.props.saveValues({
                        passwordValue: this.state.passwordTextboxValue,
                    });
                }

                this.props.onSubmit();
            }
        }

        setValidate(this, this.errors.passwordTextboxInvalidError);
    }

    private renderTextBox = () => (
        <div className={css.inputNewValue}>
            <span className={cssNavModal.standardModalLabel}>{this.props.passwordLabel}</span>
            <TextboxTracker
                {...this.props.passwordTextbox}
                focusTelemetryCustom={{
                    eventName: "usprfl-passwordEdit",
                    actionType: "Undefined",
                }}
                properties={{
                    ...this.props.passwordTextbox.properties,
                    onChange: this.onChangeAmount,
                    value: this.state.passwordTextboxValue,
                    textboxType: TextboxType.Password,
                    passwordToggle: true,
                }}
                // eslint-disable-next-line react/no-string-refs
                ref="passwordValue-textbox"
            />
            <ErrorContainer {...this.errors.passwordTextboxRequiredError.container} />
            <ErrorContainer {...this.errors.passwordTextboxInvalidError.container} />
        </div>
    );

    private closeResetPasswordForm = () => this.setState({ showResetPasswordForm: false });

    private onChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
        clearAllErrors(this);

        const { value } = event.target;

        this.setState({
            passwordTextboxValue: value,
            disabledButton: !value || value === "",
        });
    };

    private onClickForgotPassword = (ev: React.MouseEvent<HTMLAnchorElement>): void => {
        ev.preventDefault();
        this.setState({ showResetPasswordForm: true });
    };

    private submitForm = () =>
        this.ajaxRequest(
            {
                password: this.state.passwordTextboxValue,
            },
            () => this.setState({ isLoading: true }),
        );
}
